import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router, RouterLink } from '@angular/router';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, map, Subject, Subscription, switchMap, take, tap } from 'rxjs';
import { BriefStatusEnum, SearchBriefResponseDto } from '@/app/common/general-types/briefs.dto';
import { CampaignDto } from '@/app/common/general-types/campaigns.dto';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { BriefService } from '@/app/services/brief/brief.service';
import { CampaignService } from '@/app/services/campaign/campaign.service';
import { ImageService } from '@/app/services/image/image.service';
import { CreateCampaignComponent } from '@/app/molecules/create-campaign/create-campaign.component';
import { withSavedCampaign } from '@/app/pages/briefs/briefs.component';
import { BriefRow, BriefRowEvent } from './molecules/brief-row/brief-row';
import { BriefsListService } from './services/briefs-list.service';
import {
  applySavedView,
  emptyKind,
  EmptyKind,
  filterChips,
  FilterChip,
  hasActiveFilters,
  listStatusFromEnum,
  overviewCommands,
  pageWindow,
  PageWindow,
  presetViews,
  queryFromParams,
  queryToParams,
  readSavedViews,
  removeBrief,
  replaceBrief,
  sameQuery,
  sortLabel,
  tabLabel,
  toListRequest,
  typeLabel,
  writeSavedViews,
} from './services/briefs-list.helpers';
import {
  BRIEF_SORTS,
  BRIEF_TABS,
  BriefListItem,
  BriefStatus,
  BriefListQuery,
  BriefSort,
  BriefTab,
  BriefFacet,
  BriefsListResponse,
  DEFAULT_BRIEF_QUERY,
  SavedBriefView,
} from './services/briefs-list.types';
import { HasRoleDirectiveDirective } from '@/app/common/directives/has-role/has-role-directive.directive';
import { NewBriefTypeSelect } from '@/app/atoms/new-brief-type-select/new-brief-type-select';

type MenuId = 'type' | 'product' | 'owner' | 'sort' | 'views' | `row:${string}` | null;
type LoadState = 'initial' | 'refresh' | 'idle' | 'error';

@Component({
  selector: 'app-briefs-v2',
  imports: [RouterLink, BriefRow, CreateCampaignComponent, HasRoleDirectiveDirective, NewBriefTypeSelect],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './briefs-v2.html',
  styleUrl: './briefs-v2.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown)': 'onKeydown($event)',
    '(document:click)': 'onDocumentClick()',
  },
})
export class BriefsV2 implements OnInit, OnDestroy {
  protected readonly tabs = BRIEF_TABS;
  protected readonly sorts = BRIEF_SORTS;
  protected readonly tabLabel = tabLabel;
  protected readonly sortLabel = sortLabel;
  protected readonly typeLabel = typeLabel;
  protected readonly hasActiveFilters = hasActiveFilters;
  protected readonly skeletonRows = [0, 1, 2, 3, 4, 5, 6];

  protected readonly query = signal<BriefListQuery>({ ...DEFAULT_BRIEF_QUERY });
  protected readonly searchText = signal('');
  protected readonly response = signal<BriefsListResponse | null>(null);
  protected readonly loading = signal<LoadState>('initial');
  protected readonly menu = signal<MenuId>(null);
  protected readonly savedViews = signal<SavedBriefView[]>([]);
  protected readonly viewName = signal('');
  protected readonly namingView = signal(false);
  protected readonly toast = signal<{ message: string; undo: (() => void) | null } | null>(null);
  protected readonly campaigns = signal<CampaignDto[] | undefined>(undefined);
  protected readonly editingCampaign = signal(false);

  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  private readonly subscriptionList = new Subscription();
  private readonly loads = new Subject<void>();
  private readonly searchChanges = new Subject<string>();
  private readonly pendingDeletes = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly prefetched = new Set<string>();
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly listService = inject(BriefsListService);
  private readonly briefService = inject(BriefService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly campaignService = inject(CampaignService);
  protected readonly imageService = inject(ImageService);

  ngOnInit(): void {
    this.savedViews.set(readSavedViews(this.meId()));
    this.subscriptionList.add(
      this.loads.pipe(
        switchMap(() => {
          const brandId = this.currentUser.focussedBrand?.id;
          if (!brandId) {
            this.loading.set(this.response() ? 'idle' : 'initial');
            return EMPTY;
          }
          this.loading.set(this.response() ? 'refresh' : 'initial');
          return this.listService.list(toListRequest(this.query(), brandId, this.meId())).pipe(
            tap((response) => {
              console.log('Briefs studio list', response);
              this.response.set(response);
              this.loading.set('idle');
              const window = pageWindow(this.query().page, response.total);
              if (window.page !== this.query().page) {
                this.patchQuery({ page: window.page });
              }
            }),
            catchError(() => {
              this.loading.set(this.response() ? 'idle' : 'error');
              if (this.response()) {
                this.showToast('Could not refresh briefs', null);
              }
              return EMPTY;
            }),
          );
        }),
      ).subscribe(),
    );

    this.subscriptionList.add(
      this.route.queryParamMap.subscribe((params) => {
        const next = queryFromParams(paramsRecord(params));
        if (sameQuery(next, this.query())) {
          return;
        }
        this.query.set(next);
        this.searchText.set(next.q);
        this.load();
      }),
    );

    this.subscriptionList.add(
      this.searchChanges.pipe(debounceTime(150), distinctUntilChanged()).subscribe((q) => {
        if (q === this.query().q) {
          return;
        }
        this.patchQuery({ q, page: 1 });
      }),
    );

    this.subscriptionList.add(
      this.currentUser.focussedBrand$.pipe(
        map((brand) => brand?.id ?? ''),
        distinctUntilChanged(),
      ).subscribe((id) => {
        if (!id) {
          return;
        }
        this.savedViews.set(readSavedViews(this.meId()));
        this.listService.forget();
        this.loadCampaigns(id);
        this.load();
      }),
    );
  }

  ngOnDestroy(): void {
    for (const [briefId, timer] of this.pendingDeletes) {
      clearTimeout(timer);
      this.subscriptionList.add(this.briefService.deleteBrief(briefId).subscribe());
    }
    this.pendingDeletes.clear();
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
    this.subscriptionList.unsubscribe();
  }

  protected retry(): void {
    this.loading.set(this.response() ? 'refresh' : 'initial');
    this.load();
  }

  protected tabClass(tab: BriefTab): string {
    const selected = this.query().tab === tab;
    const state = selected
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
    return `inline-flex shrink-0 items-center whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium cursor-pointer ${state}`;
  }

  protected countClass(tab: BriefTab): string {
    const hot = tab === 'attn' && (this.response()?.tabCounts.attn ?? 0) > 0;
    return `ml-1.5 rounded-full px-1.5 py-0.5 text-xs font-bold ${hot ? 'bg-primary text-white' : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-300'}`;
  }

  protected typeOn(id: string): boolean {
    return this.query().types.includes(id as BriefListQuery['types'][number]);
  }

  protected typeFilterLabel(): string {
    const types = this.query().types;
    if (types.length === 0) {
      return 'All types';
    }
    if (types.length === 1) {
      return this.typeLabel(types[0]);
    }
    return `${types.length} types`;
  }

  protected productFilterLabel(): string {
    const ids = this.query().products;
    if (ids.length === 0) {
      return 'All products';
    }
    if (ids.length === 1) {
      return this.productFacets().find((facet) => facet.id === ids[0])?.name ?? '1 product';
    }
    return `${ids.length} products`;
  }

  protected ownerFilterLabel(): string {
    const ids = this.query().owners;
    if (ids.length === 0) {
      return 'All owners';
    }
    if (ids.length === 1) {
      const id = ids[0];
      if (id === 'me') {
        return 'You';
      }
      const facet = this.ownerFacets().find((item) => item.id === id);
      if (facet?.id === this.meId()) {
        return 'You';
      }
      return facet?.name ?? '1 owner';
    }
    return `${ids.length} owners`;
  }

  protected typeFacets(): BriefFacet[] {
    return this.response()?.facets?.types ?? [];
  }

  protected productFacets(): BriefFacet[] {
    return this.response()?.facets?.products ?? [];
  }

  protected ownerFacets(): BriefFacet[] {
    return this.response()?.facets?.owners ?? [];
  }

  protected allCount(): number {
    return this.response()?.tabCounts?.all ?? 0;
  }

  protected hasRows(): boolean {
    const rows = this.response()?.rows;
    return (rows?.length ?? 0) > 0;
  }

  protected meId(): string {
    return this.currentUser.shoutUser?.firebaseUid ?? '';
  }

  protected chips(): FilterChip[] {
    return filterChips(this.query(), this.response(), this.meId(), this.selectedCampaign()?.campaignName ?? '');
  }

  protected selectedCampaign(): CampaignDto | undefined {
    const id = this.query().campaign;
    if (!id) {
      return undefined;
    }
    return this.campaigns()?.find((campaign) => campaign.id === id);
  }

  protected chooseCampaign(campaign: CampaignDto): void {
    this.patchQuery({ campaign: campaign.id, page: 1 });
  }

  protected clearCampaign(): void {
    this.patchQuery({ campaign: '', page: 1 });
  }

  protected editCampaign(): void {
    if (!this.selectedCampaign()) {
      return;
    }
    this.editingCampaign.set(true);
  }

  protected closeCampaignEditor(): void {
    this.editingCampaign.set(false);
  }

  protected onCampaignSaved(campaign: CampaignDto): void {
    this.campaigns.set(withSavedCampaign(this.campaigns(), campaign));
    this.editingCampaign.set(false);
  }

  protected emptyTitle(): string {
    switch (this.query().tab) {
      case 'live':
        return 'No live briefs';
      case 'draft':
        return 'No drafts';
      case 'paused':
        return 'No paused briefs';
      case 'completed':
        return 'No completed briefs';
      default:
        return 'No briefs';
    }
  }

  protected empty(): EmptyKind | null {
    const response = this.response();
    if (!response || this.loading() === 'initial') {
      return null;
    }
    return emptyKind(this.query(), response);
  }

  protected window(): PageWindow {
    return pageWindow(this.query().page, this.response()?.total ?? 0);
  }

  protected allViews(): SavedBriefView[] {
    return [...presetViews(), ...this.savedViews()];
  }

  protected onSearch(value: string): void {
    this.searchText.set(value);
    this.searchChanges.next(value);
  }

  protected clearSearch(event?: Event): void {
    event?.stopPropagation();
    this.onSearch('');
    this.searchInput()?.nativeElement.focus();
  }

  protected setTab(tab: BriefTab): void {
    this.menu.set(null);
    this.patchQuery({ tab, page: 1 });
  }

  protected setSort(sort: BriefSort): void {
    this.menu.set(null);
    this.patchQuery({ sort, page: 1 });
  }

  protected toggleType(type: string): void {
    if (type !== 'ugc' && type !== 'screening' && type !== 'monitoring') {
      return;
    }
    this.toggleList('types', type);
  }

  protected toggleProduct(productId: string): void {
    this.toggleList('products', productId);
  }

  protected toggleOwner(ownerId: string): void {
    const token = ownerId === this.meId() ? 'me' : ownerId;
    const owners = this.query().owners.filter((id) => id !== ownerId && id !== token);
    if (!this.ownerSelected(ownerId)) {
      owners.push(token);
    }
    this.patchQuery({ owners, page: 1 });
  }

  protected ownerSelected(ownerId: string): boolean {
    const token = ownerId === this.meId() ? 'me' : ownerId;
    return this.query().owners.includes(token) || this.query().owners.includes(ownerId);
  }

  protected clearGroup(group: 'types' | 'products' | 'owners'): void {
    this.menu.set(null);
    this.patchQuery({ [group]: [], page: 1 });
  }

  protected removeChip(chip: FilterChip): void {
    if (chip.group === 'q') {
      this.clearSearch();
      return;
    }
    if (chip.group === 'type') {
      this.toggleType(chip.value as BriefListQuery['types'][number]);
      return;
    }
    if (chip.group === 'product') {
      this.toggleProduct(chip.value);
      return;
    }
    if (chip.group === 'campaign') {
      this.clearCampaign();
      return;
    }
    this.toggleOwner(chip.value === 'me' ? this.meId() : chip.value);
  }

  protected clearFilters(): void {
    this.menu.set(null);
    this.searchText.set('');
    this.patchQuery({ q: '', types: [], products: [], owners: [], campaign: '', page: 1 });
  }

  protected toggleMenu(id: Exclude<MenuId, null>, event: Event): void {
    event.stopPropagation();
    this.namingView.set(false);
    this.menu.set(this.menu() === id ? null : id);
    if (this.menu() === id) {
      queueMicrotask(() => {
        document.getElementById(`menu-${id}`)?.querySelector<HTMLButtonElement>('[role="option"], [role="menuitem"]')?.focus();
      });
    }
  }

  protected onMenuKey(event: KeyboardEvent): void {
    const menu = event.currentTarget;
    if (!(menu instanceof HTMLElement)) {
      return;
    }
    const options = [...menu.querySelectorAll<HTMLButtonElement>('[role="option"], [role="menuitem"]')];
    const index = options.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      options[Math.min(options.length - 1, index + 1)]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      options[Math.max(0, index - 1)]?.focus();
    }
  }

  protected applyView(view: SavedBriefView): void {
    this.menu.set(null);
    this.searchText.set('');
    const next = applySavedView(this.query(), view);
    this.query.set(next);
    this.writeUrl(next);
    this.load();
  }

  protected startSaveView(event: Event): void {
    event.stopPropagation();
    if (!hasActiveFilters(this.query())) {
      return;
    }
    this.namingView.set(true);
  }

  protected saveView(): void {
    const name = this.viewName().trim();
    if (!name || !hasActiveFilters(this.query())) {
      return;
    }
    const query = this.query();
    const view: SavedBriefView = {
      id: `c${Date.now()}`,
      name,
      tab: query.tab,
      types: [...query.types],
      products: [...query.products],
      owners: [...query.owners],
    };
    const next = [...this.savedViews(), view];
    this.savedViews.set(next);
    writeSavedViews(this.meId(), next);
    this.viewName.set('');
    this.namingView.set(false);
    this.menu.set(null);
    this.showToast(`Saved as “${name}”`, null);
  }

  protected setPage(delta: number): void {
    const window = this.window();
    const page = Math.min(window.pages, Math.max(1, this.query().page + delta));
    if (page === this.query().page) {
      return;
    }
    this.patchQuery({ page });
  }

  protected onSettingsUpdated(brief: BriefListItem, update: Partial<SearchBriefResponseDto>): void {
    const snapshot = this.response();
    if (!snapshot) {
      return;
    }
    this.listService.forget();
    if (update.isDeleted || update.currentStatus === BriefStatusEnum.Deleted) {
      this.response.set(removeBrief(snapshot, brief.id));
      return;
    }
    if (update.currentStatus === undefined) {
      return;
    }
    const status: BriefStatus | null = listStatusFromEnum(update.currentStatus);
    if (!status) {
      return;
    }
    const next: BriefListItem = {
      ...brief,
      status,
      pausedAt: status === 'paused' ? new Date().toISOString() : null,
    };
    this.response.set(replaceBrief(snapshot, brief.id, next, this.query().tab));
  }

  protected onRow(brief: BriefListItem, event: BriefRowEvent): void {
    switch (event) {
      case 'prefetch':
        this.prefetch(brief);
        break;
      case 'menu':
        this.menu.set(this.menu() === `row:${brief.id}` ? null : `row:${brief.id}`);
        break;
      case 'open':
        this.menu.set(null);
        void this.router.navigate(overviewCommands(brief));
        break;
      case 'setup':
        this.continueSetup(brief);
        break;
      case 'duplicate':
        this.duplicate(brief);
        break;
      case 'pause':
        this.pause(brief);
        break;
      case 'archive':
        this.archive(brief);
        break;
    }
  }

  protected undoToast(): void {
    const current = this.toast();
    current?.undo?.();
    this.dismissToast();
  }

  protected onKeydown(event: KeyboardEvent): void {
    const target = event.target instanceof HTMLElement ? event.target : null;
    const inField = !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
    if (event.key === '/' && !inField) {
      event.preventDefault();
      this.searchInput()?.nativeElement.focus();
      return;
    }
    if (event.key !== 'Escape') {
      return;
    }
    if (this.menu()) {
      event.preventDefault();
      this.menu.set(null);
      this.namingView.set(false);
      return;
    }
    if (inField && target === this.searchInput()?.nativeElement && this.searchText()) {
      event.preventDefault();
      this.clearSearch();
      return;
    }
    if (inField) {
      target?.blur();
    }
  }

  protected onDocumentClick(): void {
    this.menu.set(null);
    this.namingView.set(false);
  }

  private loadCampaigns(brandId: string): void {
    this.subscriptionList.add(
      this.campaignService.getAllBusinessCampaigns(brandId).pipe(take(1)).subscribe({
        next: (campaigns) => this.campaigns.set(campaigns ?? []),
        error: () => this.campaigns.set([]),
      }),
    );
  }

  private load(): void {
    this.loads.next();
  }

  private patchQuery(partial: Partial<BriefListQuery>): void {
    const next = { ...this.query(), ...partial };
    if (sameQuery(next, this.query())) {
      return;
    }
    this.query.set(next);
    this.writeUrl(next);
    this.load();
  }

  private writeUrl(query: BriefListQuery): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryToParams(query),
      replaceUrl: true,
    });
  }

  private toggleList(group: 'types' | 'products', value: string): void {
    const current = this.query()[group] as string[];
    const next = current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
    this.patchQuery({ [group]: next, page: 1 });
  }

  private continueSetup(brief: BriefListItem): void {
    this.menu.set(null);
    if (brief.type !== 'ugc') {
      void this.router.navigate(['/create-brief-select-smart'], { queryParams: { type: brief.type } });
      return;
    }
    this.subscriptionList.add(
      this.briefService.resumeDraft(brief.id).subscribe({
        next: () => void this.router.navigate(['/create-brief-select']),
        error: () => this.showToast('Could not open this draft', null),
      }),
    );
  }

  private duplicate(brief: BriefListItem): void {
    if (brief.type !== 'ugc') {
      return;
    }
    this.menu.set(null);
    this.subscriptionList.add(
      this.briefService.duplicateBrief(brief.id).subscribe({
        next: () => void this.router.navigate(['/create-brief-select']),
        error: () => this.showToast('Could not duplicate this brief', null),
      }),
    );
  }

  private pause(brief: BriefListItem): void {
    const snapshot = this.response();
    if (!snapshot || brief.type !== 'ugc' || (brief.status !== 'live' && brief.status !== 'paused')) {
      return;
    }
    const pausing = brief.status === 'live';
    const next: BriefListItem = {
      ...brief,
      status: pausing ? 'paused' : 'live',
      pausedAt: pausing ? new Date().toISOString() : null,
    };
    this.response.set(replaceBrief(snapshot, brief.id, next, this.query().tab));
    this.menu.set(null);
    this.listService.forget();
    const request = pausing ? this.briefService.pauseBrief(brief.id) : this.briefService.publicBrief(brief.id);
    this.subscriptionList.add(
      request.subscribe({
        next: () => {
          this.showToast(`${pausing ? 'Paused' : 'Resumed'} “${brief.name}”`, () => {
            this.response.set(snapshot);
            const inverse = pausing ? this.briefService.publicBrief(brief.id) : this.briefService.pauseBrief(brief.id);
            this.subscriptionList.add(inverse.subscribe({ error: () => this.load() }));
          });
        },
        error: () => {
          this.response.set(snapshot);
          this.showToast(`Could not ${pausing ? 'pause' : 'resume'} “${brief.name}”`, null);
        },
      }),
    );
  }

  private archive(brief: BriefListItem): void {
    const snapshot = this.response();
    if (!snapshot || brief.type !== 'ugc') {
      return;
    }
    this.response.set(removeBrief(snapshot, brief.id));
    this.menu.set(null);
    this.listService.forget();
    const timer = setTimeout(() => {
      this.pendingDeletes.delete(brief.id);
      this.subscriptionList.add(
        this.briefService.deleteBrief(brief.id).subscribe({
          error: () => {
            this.response.set(snapshot);
            this.showToast(`Could not archive “${brief.name}”`, null);
          },
        }),
      );
    }, 4000);
    this.pendingDeletes.set(brief.id, timer);
    this.showToast(`Archived “${brief.name}”`, () => {
      const pending = this.pendingDeletes.get(brief.id);
      if (pending) {
        clearTimeout(pending);
        this.pendingDeletes.delete(brief.id);
      }
      this.response.set(snapshot);
    });
  }

  private prefetch(brief: BriefListItem): void {
    if (this.prefetched.has(brief.id)) {
      return;
    }
    this.prefetched.add(brief.id);
    const path = overviewCommands(brief)[0]?.replace(/^\//, '');
    const match = this.router.config.find((item) => item.path === path);
    const load = match && 'loadComponent' in match ? match.loadComponent : undefined;
    if (typeof load === 'function') {
      void load();
    }
  }

  private showToast(message: string, undo: (() => void) | null): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
    this.toast.set({ message, undo });
    this.toastTimer = setTimeout(() => this.dismissToast(), 4000);
  }

  private dismissToast(): void {
    this.toast.set(null);
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
  }
}

function paramsRecord(params: ParamMap): Record<string, string | null> {
  const record: Record<string, string | null> = {};
  for (const key of params.keys) {
    record[key] = params.get(key);
  }
  return record;
}
